import { Brand, BrandIndexItem, BrandIndexService, BrandQuery, BrandQueryResultItem } from "@/api/braendz";
import { blobToBase64Async } from '@/models/Blob';
import { BrandStateCategory, Comparator, FilterableField, SearchableField } from "@/models/Query";
import { getBrandLogoUrl, registrationOfficeCodeRelationships } from "@/logic/Brand";

/**
 * Creates a query to find similar brands based on the specified brand.
 * @param brand The brand to find similar brands for.
 * @param limit The maximum number of results to return.
 * @param minNameSimilarityScore The minimum name similarity score to consider.
 * @param minLogoSimilarityScore The minimum logo similarity score to consider.
 * @param registrationOfficeCodes Specific registration office codes to filter by.
 * @param onlyNewBrands Whether to only include new brands.
 * @param excludedBrandIds Optional list of brand IDs to exclude from the results.
 * @param ignoredBrandIds Optional list of brand IDs to ignore in scoring.
 * @returns A promise that resolves to the constructed BrandQuery object.
 * @throws An error if the brand ID cannot be determined or if the brand is not found in the index.
 */
export async function createQueryByBrand(
    brand: BrandQueryResultItem | BrandIndexItem | Brand | string, 
    limit?: number,
    minNameSimilarityScore?: number,
    minLogoSimilarityScore?: number,
    registrationOfficeCodes?: string[],
    onlyNewBrands?: boolean,
    excludedBrandIds?: string[],
    ignoredBrandIds?: string[]): Promise<BrandQuery> {

    let brandId: string | null | undefined = undefined;
    let indexId: string | null | undefined = undefined;

    if (typeof brand === "string") {
        brandId = brand;
        indexId = brand;
    } else if("indexItem" in brand) {
        brandId = brand.indexItem?.brandId;
        indexId = brand.indexItem?.id;
    } else if ("brandId" in brand) {
        brandId = brand.brandId;
        indexId = brand.id;
    } else if ("id" in brand) {
        brandId = brand.id;
        indexId = brand.id;
        // TODO: Eventually convert indexId.
    } else {
        throw new Error("Unable to determine brand id.");
    }

    if(!brandId || !indexId) {
        throw new Error("Brand id is null or undefined.");
    }

    let brandIndexItem: BrandIndexItem | null | undefined = undefined;

    if (typeof brand === "string") {
        brandIndexItem = await BrandIndexService.getIndexItem(indexId);
    } else if ("indexItem" in brand && typeof brand.indexItem !== 'undefined') {
        brandIndexItem = brand.indexItem;
    } else if ("brandId" in brand) {
        brandIndexItem = brand;
    } else {
        brandIndexItem = await BrandIndexService.getIndexItem(indexId);
    }

    if(!brandIndexItem) {
        throw new Error("Brand not found in index.");
    }

    // Build the query:
    const query = {
        limit: limit,
        terms: [],
        filters: [],
        facets: []
    } as BrandQuery;

    // Configure scoring:
    query.scoring = { 
        enabled: true, 
        brandId: brandId,
        minNameSimilarityScore: minNameSimilarityScore,
        minLogoSimilarityScore: minLogoSimilarityScore,
    };

    // Configure terms:
    if(brandIndexItem.name) {
        query.terms?.push({ field: SearchableField.Name, value: brandIndexItem.name });
        query.terms?.push({ field: SearchableField.BrandLogoText, value: brandIndexItem.name });
    }
    else if(brandIndexItem.brandLogoText) {
        query.terms?.push({ field: SearchableField.Name, value: brandIndexItem.brandLogoText });
        query.terms?.push({ field: SearchableField.BrandLogoText, value: brandIndexItem.brandLogoText });
    }

    // Configure logo:
    const brandLogoUrl = getBrandLogoUrl(brandIndexItem);
    if(brandLogoUrl) {
        // const binaryString = await BrandImagesService.getBrandImage(btoa(this.selectedBrand.indexItem.brandLogoUrl), true);
        const blob = await fetch(brandLogoUrl).then(r => r.blob());
        query.logo = await blobToBase64Async(blob);
    }

    // Configure registration offices:
    if(registrationOfficeCodes && registrationOfficeCodes.length > 0) {
        for(const registrationOfficeCode of registrationOfficeCodes) {
            query.filters?.push({ field: FilterableField.RegistrationOfficeCode, value: registrationOfficeCode, comparator: Comparator.Equals })
        }
    }
    else if(brandIndexItem.registrationOfficeCode?.key) {
        const baseOffice = brandIndexItem.registrationOfficeCode.key;
        query.filters?.push({ field: FilterableField.RegistrationOfficeCode, value: baseOffice, comparator: Comparator.Equals });

        registrationOfficeCodeRelationships.find(o => o.key === baseOffice)?.related.forEach(relatedOffice => {
            query.filters?.push({ field: FilterableField.RegistrationOfficeCode, value: relatedOffice, comparator: Comparator.Equals });
        });
    }

    // Configure nice classes:
    if(brandIndexItem.niceClasses) {
        for(const niceClass of brandIndexItem.niceClasses) {
            query.filters?.push({ field: FilterableField.NiceClasses, value: niceClass.key, comparator: Comparator.Equals })
        }
    }

    if(onlyNewBrands) {
        query.filters?.push({ field: FilterableField.BrandStateCategory, value: BrandStateCategory.New, comparator: Comparator.Equals })
    }
    else {
        query.filters?.push({ field: FilterableField.BrandStateCategory, value: BrandStateCategory.New, comparator: Comparator.Equals })
        query.filters?.push({ field: FilterableField.BrandStateCategory, value: BrandStateCategory.Alive, comparator: Comparator.Equals })
    }

    // TODO: Clarify if we also need to add brandType-Filter.
    // Clarified: It is not necessary but maybe a nice to have for the future so that the user has the option to specify the brand type.

    // Set excluded IDs:
    query.excludedIds = excludedBrandIds;

    // Exclude monitored brand from scoring:
    query.scoring.excludedIds = [ indexId ];

    // Add other ignored brands:
    if(ignoredBrandIds) {
        for(const id of ignoredBrandIds.filter(id => id !== indexId)) {
            query.scoring.excludedIds?.push(id);
        }
    }

    return query;
}